import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from './SEO';
import Reveal from './ui/Reveal';

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const paras = (text) => (text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

const getExtra = (s) => {
  if (!s || !s.extra) return {};
  if (typeof s.extra === 'object') return s.extra;
  try {
    return JSON.parse(s.extra);
  } catch {
    return {};
  }
};

const cardGridClass = (n) => {
  if (n >= 4) return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8';
  if (n === 3) return 'grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8';
  if (n === 2) return 'grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8';
  return 'grid grid-cols-1 gap-6';
};

const DotPattern = () => (
  <div
    className="absolute inset-0 opacity-[0.04]"
    style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '24px 24px' }}
    aria-hidden
  />
);

const CenteredHead = ({ kicker, title, light, wide = false }) => (
  <div className={wide ? 'text-center' : 'text-center max-w-3xl mx-auto'}>
    {kicker && <div className={`kicker ${light ? 'text-[#F3D27E]' : ''}`}>{kicker}</div>}
    <h2
      className={`text-[26px] md:text-[32px] font-bold mt-2 leading-tight ${light ? 'text-white' : 'text-primary'}`}
    >
      {title}
    </h2>
    <div className="w-14 h-[3px] rounded-full mx-auto mt-4 bg-[#D4A73E]" />
  </div>
);

const IntroBlock = ({ section }) => {
  const extra = getExtra(section);
  const introParas = paras(section.description);

  return (
    <section className="py-16 md:py-20 bg-white relative overflow-hidden">
      <div className="absolute top-24 -right-24 w-96 h-96 rounded-full bg-[#1B3160]/5 blur-3xl" aria-hidden />
      <div className="absolute bottom-0 -left-32 w-80 h-80 rounded-full bg-[#D4A73E]/10 blur-3xl" aria-hidden />
      <div className="container relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 md:gap-16 items-center">
          <Reveal className="relative max-lg:order-2">
            <div className="absolute -inset-1 bg-gradient-to-br from-[#1B3160]/30 to-[#D4A73E]/20 rounded-3xl blur-lg opacity-60" aria-hidden />
            <img
              src={resolveImage(section.image)}
              alt={section.image_alt || section.title}
              className="relative w-full aspect-[4/3] object-cover rounded-2xl shadow-2xl"
              loading="lazy"
            />
          </Reveal>
          <div className="max-lg:order-1">
            <Reveal>
              <div className="kicker">{section.kicker}</div>
              <h2 className="text-[30px] md:text-[38px] font-bold text-primary mt-2 leading-tight">
                {section.title}
              </h2>
              <div className="w-14 h-[3px] bg-[#D4A73E] rounded-full my-6" />
            </Reveal>
            {extra.chip_text && (
              <Reveal delayMs={60}>
                <p className="inline-flex items-center gap-2 rounded-full bg-primary/5 text-primary text-sm md:text-[15px] font-semibold px-4 py-2 mb-6">
                  <i className={`fas ${extra.chip_icon || 'fa-graduation-cap'} text-[#D4A73E]`} aria-hidden />
                  {extra.chip_text}
                </p>
              </Reveal>
            )}
            <div className="space-y-5">
              {introParas.map((para, index) => (
                <Reveal key={index} delayMs={120 + index * 60}>
                  <p
                    className={`text-body text-[16px] md:text-[17px] leading-relaxed ${
                      extra.highlight === index ? 'font-semibold' : ''
                    }`}
                  >
                    {para}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StepsBlock = ({ section }) => {
  const extra = getExtra(section);
  const steps = extra.steps || [];
  const experiences = extra.experiences || [];
  const stepParas = paras(section.description);

  return (
    <section className="py-16 md:py-20 bg-light relative overflow-hidden">
      <div className="container relative">
        <Reveal>
          <CenteredHead kicker={section.kicker} title={section.title} />
        </Reveal>

        {stepParas.length > 0 && (
          <div className="text-center max-w-3xl mx-auto mt-8 space-y-4">
            <Reveal>
              {stepParas.map((p, i) => (
                <p key={i} className={`text-body text-[16px] md:text-[17px] leading-relaxed ${i > 0 ? 'mt-4' : ''}`}>
                  {p}
                </p>
              ))}
            </Reveal>
          </div>
        )}

        <Reveal delayMs={100}>
          <div className="max-w-4xl mx-auto mt-12">
            {extra.journey_label && (
              <p className="text-center text-primary font-semibold mb-8 text-[15px] md:text-base">
                {extra.journey_label}
              </p>
            )}
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              {steps.map((step, index) => (
                <div key={step} className="flex items-center gap-3">
                  <span className="flex-1 sm:flex-none bg-primary text-white font-semibold text-sm px-5 py-3 rounded-lg shadow-md flex items-center justify-center gap-2">
                    <span className="text-[#D4A73E] font-bold">{String(index + 1).padStart(2, '0')}</span>
                    {step}
                  </span>
                  {index < steps.length - 1 && (
                    <i className="fas fa-arrow-right text-[#D4A73E] text-sm hidden sm:inline" aria-hidden />
                  )}
                </div>
              ))}
            </div>
            {extra.closing && (
              <p className="text-body text-[15px] text-center mt-8">{extra.closing}</p>
            )}
          </div>
        </Reveal>

        {experiences.length > 0 && (
          <Reveal delayMs={160}>
            <div className="flex flex-wrap justify-center gap-2.5 mt-10">
              {experiences.map((exp) => (
                <span
                  key={exp}
                  className="inline-flex items-center gap-2 rounded-full border border-[#D4A73E]/50 bg-white px-5 py-2 text-sm font-semibold text-primary shadow-sm"
                >
                  <i className="fas fa-check-circle text-[#D4A73E]" aria-hidden />
                  {exp}
                </span>
              ))}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const HeaderBlock = ({ section, bgClass }) => {
  const extra = getExtra(section);
  const headerParas = paras(section.description);

  return (
    <section className={`py-16 md:py-20 ${bgClass}`}>
      <div className="container">
        <Reveal>
          <CenteredHead kicker={section.kicker} title={section.title} />
        </Reveal>

        {headerParas.length > 0 && (
          <div className="text-center max-w-3xl mx-auto mt-8 space-y-4">
            <Reveal>
              {headerParas.map((p, i) => (
                <p key={i} className={`text-body text-[16px] md:text-[17px] leading-relaxed ${i > 0 ? 'mt-4' : ''}`}>
                  {p}
                </p>
              ))}
            </Reveal>
          </div>
        )}

        {extra.closing && (
          <Reveal delayMs={160}>
            <p className="text-center text-primary font-bold text-xl md:text-[24px] mt-12">
              {extra.closing.replace(/<\/?[^>]+>/g, '')}
            </p>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const HeaderCardsBlock = ({ header, cards, bgClass }) => {
  const extra = getExtra(header);

  return (
    <section className={`py-16 md:py-20 ${bgClass}`}>
      <div className="container">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <CenteredHead kicker={header.kicker} title={header.title} />
          </div>
        </Reveal>

        <div className={cardGridClass(cards.length)}>
          {cards.map((item, index) => (
            <Reveal key={item.section_key || item.title || index} delayMs={index * 80}>
              <div className="card p-7 h-full group hover:shadow-lg transition-all duration-300 relative overflow-hidden">
                <div className="absolute -right-10 -top-10 w-28 h-28 rounded-full bg-secondary/15" aria-hidden />
                {item.icon && (
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                    <i className={`fas ${item.icon}`} aria-hidden />
                  </div>
                )}
                <h3 className="text-primary font-bold text-lg md:text-xl leading-snug mt-5">{item.title}</h3>
                {item.description && <p className="text-body text-[15px] leading-relaxed mt-3">{item.description}</p>}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CardBlock = ({ cards, bgClass }) => (
  <section className={`py-16 md:py-20 ${bgClass}`}>
    <div className="container">
      <div className={cardGridClass(cards.length)}>
        {cards.map((item, index) => (
          <Reveal key={item.section_key || item.title || index} delayMs={index * 80}>
            <div className="card p-7 h-full group hover:shadow-lg transition-all duration-300 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-28 h-28 rounded-full bg-secondary/15" aria-hidden />
              {item.icon && (
                <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                  <i className={`fas ${item.icon}`} aria-hidden />
                </div>
              )}
              <h3 className="text-primary font-bold text-lg md:text-xl leading-snug mt-5">{item.title}</h3>
              {item.description && <p className="text-body text-[15px] leading-relaxed mt-3">{item.description}</p>}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const NavyBlock = ({ section, navyCards }) => {
  const extra = getExtra(section);

  return (
    <section className="py-16 md:py-20 bg-[#1B3260] relative overflow-hidden text-white">
      <DotPattern />
      <div className="container relative">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <CenteredHead kicker={section.kicker} title={section.title} light />
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {navyCards.map((item, index) => (
            <Reveal key={item.section_key || item.title || index} delayMs={index * 70}>
              <div className="h-full text-center rounded-lg border border-white/10 bg-white/5 p-7 transition-all duration-300 hover:bg-white/10">
                {item.icon && (
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#D4A73E] text-[#0d1b3e]">
                    <i className={`fas ${item.icon}`} aria-hidden />
                  </div>
                )}
                <h3 className="text-base font-bold text-white leading-snug mt-5">{item.title}</h3>
              </div>
            </Reveal>
          ))}
        </div>

        {extra.footer && (
          <Reveal delayMs={150}>
            <p className="text-center text-white/80 text-[16px] md:text-[17px] leading-relaxed max-w-3xl mx-auto mt-10">
              {extra.footer}
            </p>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const SplitBlock = ({ section }) => {
  const extra = getExtra(section);
  const splitParas = paras(section.description);
  const navy = extra.bg !== 'white';

  return (
    <section className={`py-16 md:py-20 ${navy ? 'bg-[#1B3260]' : 'bg-white'} relative overflow-hidden ${navy ? 'text-white' : ''}`}>
      {navy && <DotPattern />}
      <div className="container relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 md:gap-16 items-center">
          <div>
            <Reveal>
              <div className={`kicker ${navy ? 'text-[#F3D27E]' : ''}`}>{section.kicker}</div>
              <h2 className={`text-[26px] md:text-[32px] font-bold mt-2 leading-tight ${navy ? 'text-white' : 'text-primary'}`}>
                {section.title}
              </h2>
              <div className="w-14 h-[3px] bg-[#D4A73E] rounded-full mt-4 mb-6" />
            </Reveal>
            <div className="space-y-5">
              {splitParas.map((para, index) => (
                <Reveal key={index} delayMs={100 + index * 60}>
                  <p className={`text-[16px] md:text-[17px] leading-relaxed ${navy ? 'text-white/80' : 'text-body'}`}>
                    {para}
                  </p>
                </Reveal>
              ))}
            </div>
            {extra.gold_line && (
              <Reveal delayMs={280}>
                <p className="text-[#F3D27E] text-[16px] md:text-[17px] font-bold leading-relaxed">
                  {extra.gold_line}
                </p>
              </Reveal>
            )}
          </div>
          <Reveal className="relative max-lg:order-1">
            <div className="absolute -inset-1 bg-gradient-to-br from-[#D4A73E]/40 to-[#1B3160]/30 rounded-3xl blur-lg opacity-60" aria-hidden />
            <img
              src={resolveImage(section.image)}
              alt={section.image_alt || section.title}
              className="relative w-full aspect-[4/3] object-cover rounded-2xl shadow-2xl"
              loading="lazy"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const OutcomeBlock = ({ section }) => {
  const outcomeParas = paras(section.description);

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container">
        <Reveal>
          <CenteredHead kicker={section.kicker} title={section.title} />
        </Reveal>
        {outcomeParas.length > 0 && (
          <Reveal delayMs={100}>
            <div className="text-body text-[16px] md:text-[17px] leading-relaxed max-w-3xl mx-auto text-center mt-8 space-y-4">
              {outcomeParas.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>
        )}
        {section.image && (
          <Reveal delayMs={120}>
            <div className="mt-10 rounded-2xl overflow-hidden shadow-2xl max-w-4xl mx-auto">
              <img
                src={resolveImage(section.image)}
                alt={section.image_alt || section.title}
                className="w-full aspect-[16/9] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const CtaBlock = ({ section }) => {
  const extra = getExtra(section);

  return (
    <section className="py-16 md:py-20 bg-[#1B3260] relative overflow-hidden text-white">
      <DotPattern />
      <div className="container relative">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <i className={`fas ${section.icon || 'fa-comment-dots'} mx-auto mb-6 text-4xl text-[#F3D27E]`} aria-hidden />
            <h2 className="text-[26px] md:text-[34px] font-bold text-white leading-tight">{section.title}</h2>
            <p className="text-white/70 text-[16px] md:text-[17px] leading-relaxed mt-4">{section.description}</p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
              <Link to={section.button_url || '/our-programmes'} className="btn-gold">
                {section.button_text || 'Explore Our Programmes'}
                <i className="fas fa-arrow-right text-[12px]" aria-hidden />
              </Link>
              <Link
                to={section.button2_url || '/admission'}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-semibold border border-white/30 text-white bg-white/10 backdrop-blur hover:bg-white/15 hover:border-white/40 transition-all duration-300"
              >
                {section.button2_text || 'Enquire for Admission'}
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const buildBlocks = (sections) => {
  const blocks = [];
  for (let i = 0; i < sections.length; i++) {
    const s = sections[i];
    if (s.section_type === 'navy') {
      const navyCards = [];
      while (i + 1 < sections.length && sections[i + 1].section_type === 'navy_card') {
        navyCards.push(sections[i + 1]);
        i++;
      }
      blocks.push({ type: 'navy', section: s, navyCards });
    } else if (s.section_type === 'card') {
      const cards = [];
      while (i < sections.length && sections[i].section_type === 'card') {
        cards.push(sections[i]);
        i++;
      }
      i--;
      const prev = blocks[blocks.length - 1];
      if (prev && prev.type === 'header') {
        blocks.pop();
        const bgClass = getExtra(prev.section).bg === 'light' ? 'bg-light' : 'bg-white';
        blocks.push({ type: 'headerCards', header: prev.section, cards, bgClass });
      } else {
        blocks.push({ type: 'card', cards, bgClass: 'bg-white' });
      }
    } else {
      blocks.push({ type: s.section_type, section: s });
    }
  }
  return blocks;
};

const ProgrammePageRenderer = ({ pageKey, defaults, seo }) => {
  const [sections, setSections] = useState(defaults);

  useEffect(() => {
    let mounted = true;
    const fetchSections = async () => {
      try {
        const res = await API.get(`/programme-pages/${pageKey}/sections`);
        if (!mounted) return;
        if (res.data?.data?.length) {
          setSections(res.data.data);
        }
      } catch (err) {
        /* fall back to defaults */
      }
    };
    fetchSections();
    return () => { mounted = false; };
  }, [pageKey]);

  const blocks = buildBlocks(sections);
  const hero = blocks.find((b) => b.type === 'hero');

  return (
    <>
      <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />

      {hero && (
        <section className="page-hero">
          <img
            src={resolveImage(hero.section.image)}
            alt={hero.section.image_alt || seo.title}
            className="page-hero-img"
            loading="eager"
          />
          <div className="page-hero-content">
            <div className="container">
              <div className="page-hero-inner" />
            </div>
          </div>
        </section>
      )}

      {blocks
        .filter((b) => b.type !== 'hero')
        .map((block, index) => {
          switch (block.type) {
            case 'intro':
              return <IntroBlock key={index} section={block.section} />;
            case 'steps':
              return <StepsBlock key={index} section={block.section} />;
            case 'header':
              return <HeaderBlock key={index} section={block.section} bgClass={getExtra(block.section).bg === 'light' ? 'bg-light' : 'bg-white'} />;
            case 'headerCards':
              return <HeaderCardsBlock key={index} header={block.header} cards={block.cards} bgClass={block.bgClass} />;
            case 'card':
              return <CardBlock key={index} cards={block.cards} bgClass={block.bgClass} />;
            case 'navy':
              return <NavyBlock key={index} section={block.section} navyCards={block.navyCards} />;
            case 'split':
              return <SplitBlock key={index} section={block.section} />;
            case 'outcome':
              return <OutcomeBlock key={index} section={block.section} />;
            case 'cta':
              return <CtaBlock key={index} section={block.section} />;
            default:
              return null;
          }
        })}
    </>
  );
};

export default ProgrammePageRenderer;